import { useEffect } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { isTauri } from "@/lib/is-tauri";
import {
  isDesktopGateBypassedForE2E,
  shouldShowDesktopPrompt,
} from "@/lib/desktop-download";
import { AuthLoadingState } from "./auth-loading-state";
import { navigateToExternalNext, resolveSafeNext } from "./next-redirect";

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Guards authenticated routes. Beyond the auth check, this is the single
 * enforcement point for the desktop-handoff gate (see the
 * `shouldShowDesktopPrompt` redirect below): every authenticated-user-facing
 * route MUST be wrapped in `AuthGuard`. The web canvas is desktop-only —
 * any browser visitor is routed to /auth/desktop-handoff.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Tauri boots over a transparent glass window and never shows the
    // login page in-app — painting the auth layout here flashes
    // a full-screen gradient until getUser() resolves. Stay transparent,
    // matching AuthGate's own pre-auth placeholder.
    if (isTauri()) return <div className="h-screen w-screen" />;
    return <AuthLoadingState />;
  }

  if (!isAuthenticated) {
    // In Tauri the desktop AuthGate is the source of truth — if we
    // rendered here, it accepted a Keychain token. A transient null
    // user (slow getUser, brief 401) must not bounce to /auth/login;
    // Tauri redirects /auth/* → / which would loop. AuthGate will
    // unmount us via AUTH_INVALID_EVENT if the token is truly bad.
    if (isTauri()) return <>{children}</>;
    return <Navigate to="/auth/login" replace />;
  }

  if (shouldShowDesktopPrompt() && !isDesktopGateBypassedForE2E()) {
    const next = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/auth/desktop-handoff?next=${encodeURIComponent(next)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}

/**
 * Redirect authenticated users away from auth pages (login, register, etc.).
 * Honors a validated `?next=` so flows that land an already-signed-in user
 * on an auth page (register → verify → login chain) continue to their
 * destination instead of being dropped on `/`.
 */
export function GuestGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const safeNext = isAuthenticated
    ? resolveSafeNext(searchParams.get("next"))
    : null;
  // Backend-handled `next` can't go through React Router: cross-origin
  // URLs (desktop authorize bridge on a separate API host) AND
  // same-origin `/api/*` paths (prod + dev serve the SPA and API from one
  // origin, so the desktop authorize URL parses as same-origin — routing
  // it through <Navigate> would land on a blank non-route). Both need a
  // full top-level page load so cookies travel and the browser follows
  // the backend's redirect chain — for the desktop loopback flow that
  // chain ends at `http://127.0.0.1:<port>/cb`, a cross-scheme hop a
  // `fetch` cannot follow. Same pattern as the login submit handler.
  const isBackendNext =
    safeNext !== null &&
    (new URL(safeNext).origin !== window.location.origin ||
      new URL(safeNext).pathname.startsWith("/api/"));

  // If the navigation is silently blocked, fall back to `/` instead of
  // leaving the user on a permanent spinner.
  useEffect(() => {
    if (!isBackendNext || !safeNext) return;
    const fallback = window.setTimeout(
      () => navigate("/", { replace: true }),
      5000,
    );
    navigateToExternalNext(safeNext);
    return () => window.clearTimeout(fallback);
  }, [isBackendNext, safeNext, navigate]);

  if (isLoading || isBackendNext) {
    return <AuthLoadingState />;
  }

  if (isAuthenticated) {
    if (safeNext) {
      const target = new URL(safeNext);
      return <Navigate to={target.pathname + target.search} replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
